import { positionToStreamerSymbol } from "@/utils/option-symbol-utils";
import { quotesCacheService } from "@/services/quotes-cache-service";

type QuoteLike = {
  bidPrice?: number | null;
  askPrice?: number | null;
  lastPrice?: number | null;
  midPrice?: number | null;
  volume?: number | null;
  timestamp?: number | null;
};

interface ResolveQuoteParams {
  quoteMap: Map<string, QuoteLike>;
  positionSymbol?: string | null;
  streamerSymbol?: string | null;
  additionalSymbols?: Array<string | null | undefined>;
  useCacheFallback?: boolean;
}

const addCandidate = (set: Set<string>, raw: string | null | undefined): void => {
  if (raw == null) {
    return;
  }

  const candidates: string[] = [];

  if (typeof raw === "string") {
    if (raw.length) {
      candidates.push(raw);
    }

    const trimmed = raw.trim();
    if (trimmed && trimmed !== raw) {
      candidates.push(trimmed);
    }

    const upper = trimmed.toUpperCase();
    if (upper && upper !== trimmed) {
      candidates.push(upper);
    }

    const collapsed = trimmed.replace(/\s+/g, "");
    if (collapsed && collapsed !== trimmed) {
      candidates.push(collapsed);

      const collapsedUpper = collapsed.toUpperCase();
      if (collapsedUpper && collapsedUpper !== collapsed) {
        candidates.push(collapsedUpper);
      }
    }

    if (trimmed.startsWith('.')) {
      const withoutDot = trimmed.slice(1);
      if (withoutDot) {
        candidates.push(withoutDot);
        const withoutDotUpper = withoutDot.toUpperCase();
        if (withoutDotUpper && withoutDotUpper !== withoutDot) {
          candidates.push(withoutDotUpper);
        }
      }
    }
  }

  candidates.forEach((candidate) => {
    if (candidate.length > 0) {
      set.add(candidate);
    }
  });
};

const toQuoteLike = (quote: QuoteLike | null): QuoteLike | null => {
  if (!quote) {
    return null;
  }

  return {
    bidPrice: quote.bidPrice ?? null,
    askPrice: quote.askPrice ?? null,
    lastPrice: quote.lastPrice ?? null,
    midPrice: quote.midPrice ?? null,
    volume: quote.volume ?? null,
    timestamp: quote.timestamp ?? null,
  };
};

const fromCachedQuote = (symbol: string): QuoteLike | null => {
  const cached = quotesCacheService.getQuote(symbol);
  if (!cached) {
    return null;
  }

  return {
    bidPrice: cached.bidPrice ?? null,
    askPrice: cached.askPrice ?? null,
    lastPrice: cached.lastPrice ?? null,
    midPrice: cached.midPrice ?? null,
    volume: cached.volume ?? null,
    timestamp: cached.timestamp ?? null,
  };
};

export const resolveQuoteForPosition = ({
  quoteMap,
  positionSymbol,
  streamerSymbol,
  additionalSymbols = [],
  useCacheFallback = true,
}: ResolveQuoteParams): QuoteLike | null => {
  const candidates = new Set<string>();

  addCandidate(candidates, streamerSymbol);
  addCandidate(candidates, positionSymbol);

  if (positionSymbol) {
    addCandidate(candidates, positionToStreamerSymbol(positionSymbol));
  }

  additionalSymbols.forEach((candidate) => {
    addCandidate(candidates, candidate ?? null);
  });

  if (positionSymbol?.includes("PYPL260320C100")) {
    console.log('[QuoteResolver] Inspecting PYPL260320C100', {
      positionSymbol,
      streamerSymbol,
      candidates: Array.from(candidates),
    });
  }

  for (const candidate of candidates) {
    const directQuote = quoteMap.get(candidate);
    if (directQuote) {
      if (positionSymbol?.includes("PYPL260320C100")) {
        console.log('[QuoteResolver] Direct match for PYPL contract', {
          candidate,
          quote: directQuote,
        });
      }
      return toQuoteLike(directQuote);
    }
  }

  if (useCacheFallback) {
    for (const candidate of candidates) {
      const cachedQuote = fromCachedQuote(candidate);
      if (cachedQuote) {
        if (positionSymbol?.includes("PYPL260320C100")) {
          console.log('[QuoteResolver] Cache fallback match for PYPL contract', {
            candidate,
            quote: cachedQuote,
          });
        }
        return cachedQuote;
      }
    }
  }

  if (positionSymbol?.includes("PYPL260320C100")) {
    console.log('[QuoteResolver] No quote found for PYPL contract', {
      positionSymbol,
      candidates: Array.from(candidates),
    });
  }

  return null;
};

export const computeBestPrice = (quote: QuoteLike | null | undefined): number | null => {
  if (!quote) {
    return null;
  }

  const isValid = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value) && value > 0;

  if (isValid(quote.midPrice)) {
    return quote.midPrice;
  }

  if (isValid(quote.bidPrice) && isValid(quote.askPrice)) {
    const mid = (quote.bidPrice + quote.askPrice) / 2;
    if (isValid(mid)) {
      return mid;
    }
  }

  if (isValid(quote.lastPrice)) {
    return quote.lastPrice;
  }

  if (isValid(quote.askPrice)) {
    return quote.askPrice;
  }

  if (isValid(quote.bidPrice)) {
    return quote.bidPrice;
  }

  return null;
};

export type { QuoteLike };
